import { useCallback, useMemo, useRef } from "react";
import type { ColDef, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { PurchaseOrderItem } from "../models/RollsReceived";

interface Props {
	items: PurchaseOrderItem[];
	selectedId: number | null;
	onSelect: (item: PurchaseOrderItem) => void;
}

export const PurchaseOrderItemsTable = ({
	items,
	selectedId,
	onSelect,
}: Props) => {
	const gridRef = useRef<AgGridReact<PurchaseOrderItem>>(null);

	const columnDefs = useMemo<ColDef<PurchaseOrderItem>[]>(
		() => [
			{
				field: "ItemNumber",
				headerName: "Item #",
				flex: 0.6,
				minWidth: 70,
				cellStyle: { textAlign: "center" },
			},
			{
				field: "RollCost",
				headerName: "Cost",
				flex: 0.8,
				minWidth: 80,
				cellStyle: { textAlign: "right" },
				valueFormatter: (p) =>
					p.value != null ? Number(p.value).toFixed(2) : "",
			},
			{
				headerName: "Hardness",
				flex: 0.8,
				minWidth: 85,
				cellStyle: { textAlign: "center" },
				valueGetter: (p) =>
					`${p.data?.HardnessMinimum ?? ""}/${p.data?.HardnessMaximum ?? ""}`,
			},
			{
				field: "RollBlankDiameter",
				headerName: "Blank Diameter",
				flex: 1,
				minWidth: 110,
				cellStyle: { textAlign: "right" },
				valueFormatter: (p) =>
					p.value != null ? Number(p.value).toFixed(3) : "",
			},
			{
				field: "QuantityOrdered",
				headerName: "Qty Ordered",
				flex: 0.9,
				minWidth: 95,
				cellStyle: { textAlign: "center" },
			},
			{
				field: "RollsReceived",
				headerName: "Received",
				flex: 0.9,
				minWidth: 90,
				cellStyle: { textAlign: "center" },
			},
		],
		[],
	);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<PurchaseOrderItem>) => {
			if (event.data) onSelect(event.data);
		},
		[onSelect],
	);

	const getRowId = useCallback(
		(params: { data: PurchaseOrderItem }) => params.data.Id.toString(),
		[],
	);

	return (
		<AgGridReact<PurchaseOrderItem>
			ref={gridRef}
			rowData={items}
			columnDefs={columnDefs}
			defaultColDef={globalDefaultColDef}
			onRowClicked={onRowClicked}
			getRowId={getRowId}
			rowSelection="single"
			suppressContextMenu
			animateRows={false}
			rowHeight={32}
			headerHeight={32}
			getRowStyle={(params) =>
				selectedId === params.data?.Id
					? { backgroundColor: "#e3f2fd" }
					: undefined
			}
		/>
	);
};
